import type { PolicyOut } from '@repo/api-codegen';
import { useApiListPolicies } from '@repo/api-codegen';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { TableV2 } from '../../components/TableV2';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

import { trackEvent } from '@repo/common/analytics';
import { FilterOptionType } from '@repo/common/components/Filter/types';
import type { ApiCatalogSort } from '@repo/common/interfaces/params';
import { useAuthUser } from '../../api';
import { usePolicyWritePermission } from '../../api/hooks/policy';
import { FILTER_OPTIONS_CONFIG } from '../../components/Filter/constants';
import { useActions, useColumns } from './PoliciesList.hooks';

const COLUMN_VISIBILITY = {
	catalogServerType: 'policies',
	catalogType: 'policies',
} as const;

const DEFAULT_SORT: ApiCatalogSort = {
	field: 'severity',
	order: 'desc',
};

const PoliciesList = observer(
	({
		withAdditionalButtons,
		defaultRequiredSearchParams,
	}: {
		withAdditionalButtons?: React.ReactNode;
		defaultRequiredSearchParams?: Record<string, any>;
	}) => {
		const { user, workspace } = useAuthUser();
		const QUICK_ACTIONS = ['actions::delete', 'actions::duplicate'] as const;
		const hasWritePermission = usePolicyWritePermission();

		const columns = useColumns();
		const actions = useActions();

		const navigate = useNavigate();

		const handleClick = useCallback(
			(id: string) => {
				navigate(`/policy/${id}`);
				trackEvent('policy/open', { policy_id: id }, user, workspace);
			},
			[navigate, user, workspace]
		);

		const onCellClick = useCallback(
			(cell: {
				column: ExtendedDataTableColumn<PolicyOut>;
				record: { id: string };
			}) => {
				if (cell.column.navigate !== false) {
					handleClick(cell.record.id);
				}
			},
			[handleClick]
		);

		return (
			<TableV2<PolicyOut>
				pluralTypeString="policies"
				tableCacheKey="policies"
				columns={columns}
				columnVisibility={COLUMN_VISIBILITY}
				// @ts-ignore
				usePaginationList={useApiListPolicies}
				useCodegenListInterface
				withActions={actions}
				withQuickActions={QUICK_ACTIONS}
				withFilterOptions={[
					FILTER_OPTIONS_CONFIG[FilterOptionType.SEVERITY],
					FILTER_OPTIONS_CONFIG[FilterOptionType.FRAMEWORK],
					FILTER_OPTIONS_CONFIG[FilterOptionType.ENABLED],
					FILTER_OPTIONS_CONFIG[FilterOptionType.POLICY_STATUS],
				]}
				withCheckbox={hasWritePermission}
				withCsvExport
				withInteractiveHeader
				withSearch
				defaultSort={DEFAULT_SORT}
				withAdditionalButtons={withAdditionalButtons}
				defaultRequiredSearchParams={defaultRequiredSearchParams}
				onCellClick={onCellClick}
			/>
		);
	}
);

PoliciesList.displayName = 'PoliciesList';
export default PoliciesList;
